import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { NextPage } from 'next';

import { injectAppContextGetServerSideProps } from '../../../../_app';
import {
  fetchMerchantDetail,
  fetchProductDetail,
} from '../../../../../infrastructure/api';
import { MerchantProductStatus } from '../../../../../interfaces/merchants/products';
import { MerchantDetail } from '../../../../../interfaces/merchants';

import MetaTags from '../../../../../components/layouts/MetaTags';
import MerchantProductPurchasePage from '../../../../../templates/merchants/products/purchases/IndexTemplate';
import ErrorTemplate from '../../../../../templates/ErrorTemplate';
import { ProductRepositoryForSSR } from '../../../../../infrastructure/repositories/ProductRepository';
import MerchantProductProtectedPage from '../../../../../molecules/merchant/products/MerchantProductProtectedPage';
import { MerchantSite } from '../../../../../components/MerchantSite';
import { usePushMerchantDataLayerEffect } from '../../../../../hooks/gtm';
import { fetchUsersSubscription } from '../../../../../redux/actions/users/api';
import { useRouter } from 'next/router';
import { buildSiteUrl } from 'src/utils/request-url';

interface Query {
  publicId: string;
  productId: string;
}

interface Props {
  session: string;
  publicId: string;
  merchantDetail?: MerchantDetail;
  productId: string;
  productStatus: MerchantProductStatus;
  isUnpublishedSite: boolean;
  isPasswordProtected?: boolean;
  isCoubicApp: boolean;
}

const subscribedBy = async (productId: string) => {
  const userSubscription = await fetchUsersSubscription(productId);
  return !!userSubscription;
};

const MerchantProductPurchase: NextPage<Props> = (props: Props) => {
  const {
    merchantDetail,
    isUnpublishedSite,
    isPasswordProtected,
    publicId,
    productId,
    productStatus,
    isCoubicApp,
  } = props;
  const intl = useIntl();
  const router = useRouter();
  const [passwordProtected, setPasswordProtected] =
    useState(isPasswordProtected);
  const onPasswordSubmit = (isCorrectPassword: boolean) => {
    if (isCorrectPassword) {
      setPasswordProtected(false);
    }
  };
  usePushMerchantDataLayerEffect({ visit_merchant_id: merchantDetail?.id });

  useEffect(() => {
    const subscribedCheck = async () => {
      const subscribed = await subscribedBy(productId);
      if (subscribed) {
        router.push(buildSiteUrl('', 'next'));
      }
    };
    subscribedCheck();
  }, [productId, router]);

  return (
    <MerchantSite
      isUnPublished={isUnpublishedSite}
      isMerchantInfoExist={!!merchantDetail}
      isProtected={passwordProtected}
      protectedElement={
        <MerchantProductProtectedPage
          publicId={publicId}
          productPublicId={productId}
          onSubmit={onPasswordSubmit}
        />
      }
      logoImage={merchantDetail?.logo_image || null}
      merchantPublicId={merchantDetail?.public_id || null}
      displayName={merchantDetail?.display_name || null}
      isCoubicApp={isCoubicApp}
    >
      {merchantDetail ? (
        <>
          <MetaTags
            title={intl.formatMessage({ id: 'purchases.meta.title' })}
            noIndex={true}
            googleAnalyticsFourTrackingId={
              merchantDetail.available_google_analytics_four_tracking_id
            }
            faviconImageUrl={merchantDetail?.favicon_image?.url}
          />
          <MerchantProductPurchasePage
            publicId={publicId}
            merchantDetail={merchantDetail}
            productId={productId}
            productStatus={productStatus}
            isCoubicApp={isCoubicApp}
          />
        </>
      ) : (
        <ErrorTemplate statusCode={404} />
      )}
    </MerchantSite>
  );
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (context) => {
    const { query, session, token, requestId, isCoubicApp } = context;
    const { publicId, productId } = query as unknown as Query;

    const merchantDetailResponse = await fetchMerchantDetail({
      session,
      token,
      requestId,
      merchantPublicId: publicId,
    });
    if (merchantDetailResponse.redirect) {
      return { redirect: merchantDetailResponse.redirect };
    }
    const { merchant, isUnpublishedSite } = merchantDetailResponse;

    if (isUnpublishedSite) {
      return {
        props: {
          session,
          token,
          requestId,
          publicId,
          merchantDetail: merchant,
          productId,
          productStatus: MerchantProductStatus.Unpublished, // 予約サイトがunpublishedなので、productも固定でunpublishedにしている
          isUnpublishedSite,
          isPasswordProtected: false,
          isCoubicApp,
        },
      };
    }

    const repository = ProductRepositoryForSSR(session, token, requestId);
    const [productDetail] = await Promise.all([
      fetchProductDetail({
        session,
        token,
        requestId,
        merchantPublicId: publicId,
        productPublicId: productId,
      }),
      repository.IsProtected(publicId, productId),
    ]);

    const isSubscription = productDetail.scheme === 0;

    return {
      redirect: {
        destination: buildSiteUrl(
          `/${
            isSubscription ? 'subscriptions' : 'ticket_books'
          }/${productId}/purchase`,
          'next',
        ),
        permanent: false,
      },
    };
  },
);

export default MerchantProductPurchase;
